<script setup lang="ts">
import { logApiError } from '~/utils/logger'
import { sanitizeHtml } from '~/utils/sanitize'

const boyWithLogo = '/images/logo.png'

interface Faq {
  id: string
  question: string
  answer: string
  category: string | null
  display_order: number
  is_active: boolean
}

// Composable
const { getFaqs } = useFaqs()

// State
const faqs = ref<Faq[]>([])
const loading = ref(false)

// Fetch active FAQs ordered by display_order
async function fetchFaqs() {
  loading.value = true
  try {
    const { data } = await getFaqs({
      isActive: true,
      sortBy: 'display_order',
      sortOrder: 'asc',
      limit: 100, // Show all active FAQs
    })

    faqs.value = data
  }
  catch (error) {
    logApiError('/api/faqs', error, { operation: 'fetchFaqs', filters: { isActive: true } })
  }
  finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchFaqs()
})
</script>

<template>
  <div id="faq">
    <VContainer>
      <!-- 👉 Header  -->
      <div class="faq-section">
        <div class="headers d-flex justify-center flex-column align-center">
          <VChip
            label
            color="primary"
            size="x-large"
            class="ma-2"
            style="font-size: 1rem;font-weight: 600;text-transform: uppercase;"
          >
            FAQ
          </VChip>
          <h4 class="d-flex align-center text-h4 mb-1 flex-wrap justify-center">
            Veelgestelde
            <div class="position-relative ms-2">
              <div class="section-title">
                vragen
              </div>
            </div>
          </h4>
          <p class="text-body-1 mb-0">
            Bekijk deze veelgestelde vragen om antwoorden te vinden op veel gestelde vragen.
          </p>
        </div>
        <VRow>
          <VCol
            cols="12"
            md="5"
          >
            <div class="pt-10 d-flex align-center justify-center h-100">
              <VImg
                style="border-radius: 6px 6px 0 0;"
                :src="boyWithLogo"
                height="300"
                width="330"
              />
            </div>
          </VCol>
          <VCol
            cols="12"
            md="7"
          >
            <VExpansionPanels
              v-if="!loading && faqs.length"
              class="pt-16"
            >
              <VExpansionPanel
                v-for="faq in faqs"
                :key="faq.id"
              >
                <VExpansionPanelTitle>
                  <span
                    v-if="faq.category"
                    class="text-caption text-primary me-2"
                  >[{{ faq.category }}]</span>
                  {{ faq.question }}
                </VExpansionPanelTitle>
                <VExpansionPanelText>
                  <div class="faq-richtext" v-html="sanitizeHtml(faq.answer)" />
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>

            <!-- Loading State -->
            <div
              v-if="loading"
              class="pt-16 d-flex justify-center"
            >
              <VProgressCircular
                indeterminate
                color="primary"
              />
            </div>

            <!-- Empty State -->
            <div
              v-if="!loading && !faqs.length"
              class="pt-16 text-center"
            >
              <p class="text-body-1 text-disabled">
                Geen FAQ's gevonden
              </p>
            </div>
          </VCol>
        </VRow>
      </div>
    </VContainer>
  </div>
</template>

<style lang="scss" scoped>
.faq-section {
  margin-block: 5.25rem;
}

@media (max-width: 600px) {
  .faq-section {
    margin-block: 4rem;
  }
}

#faq {
  position: relative;
  overflow: hidden;
  border-radius: 3.75rem 3.75rem 0 0;
}

#faq::before {
  position: absolute;
  z-index: 0;

  /* Use WebP with PNG fallback for 86% file size reduction (5MB → 725KB) */
  background-image: url("/images/background.webp");
  background-image:
    image-set(
      url("/images/background.webp") type("image/webp"),
      url("/images/background.png") type("image/png")
    );
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  content: "";
  inset: 0;
  opacity: 0.1;
  pointer-events: none;
}

.section-title {
  font-size: 24px;
  font-weight: 800;
  line-height: 36px;
}

.section-title::after {
  position: absolute;

  /* background: url("@images/front-pages/icons/section-title-icon.png") no-repeat left bottom; */
  background-size: contain;
  block-size: 100%;
  content: "";
  font-weight: 800;
  inline-size: 130%;
  inset-block-end: 12%;
  inset-inline-start: -12%;
}

.faq-richtext :deep(p) {
  font-size: 0.95rem !important;
  line-height: 1.5 !important;
  margin-block: 0.35rem !important;
}

.faq-richtext :deep(p:first-child) {
  margin-block-start: 0 !important;
}

.faq-richtext :deep(p:last-child) {
  margin-block-end: 0 !important;
}
</style>
